import { useState } from 'react';
import { z } from 'zod';
import { TextBloc } from '@/components/TextBlock';
import { TitleColumn } from '@/components/TitleColumn';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetData } from '@/hooks/useWidgetData';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { fontOptionsSchema, fontStyleToCss } from '@/lib/fontStyle';
import { placeholderText } from '@/lib/text';
import { warnOnce } from '@/lib/warnOnce';
import type { WeatherHour } from '@/store/transforms';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A six-hour forecast strip — hour, icon, temperature — beside a title/text column. Port of
 * `weather/weatherWidget.vue`.
 *
 * Used by `szeged_2`, `szeged_3` and `szeged_4`, all three with the same `all/weather` topic
 * and a 1.4rem header/footer font.
 *
 * The data arriving here is already finished: `weatherTransform` (SYM-490) slices `hourly` to
 * six entries and zero-pads the local hour. This widget never sees the raw payload and must
 * not reshape one — the transform is wired once, by `WidgetSlot`.
 *
 * **W-7 fixed.** The legacy template puts `v-if="values && values.length"` and
 * `v-for="forecast in values"` on the same element, which Vue resolves by running `v-if`
 * *first* — so the guard reads `values` before the loop variable exists. It happens to work
 * because the two names differ; it is a lint error in every Vue style guide and it is simply
 * absent in JSX, where an empty array maps to nothing.
 */

const optionsSchema = z.object({
  topic: z.string().optional(),
  layout: widgetLayoutSchema.optional(),
  title: z.string().optional(),
  titleFont: fontOptionsSchema.optional(),
  textBloc: z.object({ text: z.string().default('') }).prefault({}),
  textBlocFont: fontOptionsSchema.optional(),
  /** The two per-cell font blocks: `header` is the hour, `footer` the temperature. */
  component: z
    .object({
      header: fontOptionsSchema.optional(),
      footer: fontOptionsSchema.optional(),
    })
    .prefault({}),
});

/**
 * The **transform's output**, not the broker payload (docs/MIGRATION-WIDGETS.md §7 covers the
 * latter). Validating it again here is not redundancy for its own sake: a widget rendered
 * outside `WidgetSlot`, or a config whose `type` was changed after the topic was claimed, can
 * put an unshaped payload in the store, and the difference between "no forecast" and a thrown
 * `undefined.hour` on a wall is this schema.
 */
const forecastSchema = z.array(z.object({ temp: z.number(), icon: z.string(), hour: z.string() }));

/** The only remote asset in the app — see `WeatherIcon`. */
const ICON_BASE_URL = 'https://openweathermap.org/img/wn/';

export function WeatherWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const data = useWidgetData<unknown>(componentId, opts?.topic);
  const { className, style } = useWidgetLayout(opts?.layout);

  if (!parsed.success) {
    warnOnce(
      `weather-widget-options:${componentId}`,
      '[signage] WeatherWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const opt = parsed.data;
  const forecast = readForecast(data, componentId);

  const headerFont = fontStyleToCss(opt.component.header, { fontSize: '1.2rem' });
  const footerFont = fontStyleToCss(opt.component.footer, { fontSize: '1.2rem' });

  return (
    <div className={className} style={style}>
      <div className="w-full flex justify-between">
        <TitleColumn className="w-1/4" title={opt.title} titleFont={opt.titleFont}>
          {/*
           * The text block never resolves its `{{variables}}`, and that is the legacy
           * behaviour rather than an omission: `weatherWidget.vue` computes the placeholder
           * form once in `setup()` and has no `watch` to update it — and it could not have
           * one, because by the time the payload reaches this widget the transform has
           * replaced it with a `WeatherHour[]` that has no keys to substitute from.
           * docs/MIGRATION-WIDGETS.md §3 lists this widget under the `'strip'` group, which
           * is what `placeholderText` implements. No deployed config puts a variable here.
           */}
          <TextBloc text={placeholderText(opt.textBloc.text)} font={opt.textBlocFont} />
        </TitleColumn>
        {/* Empty until the first message: docs/MIGRATION-WIDGETS.md §9, "forecast strip absent". */}
        <div className="w-3/4 flex flex-wrap justify-end">
          {forecast.map((hour, index) => (
            <div key={index} className="flex flex-col px-5 py-2 items-center">
              <span className="textHeader" style={headerFont}>
                {hour.hour}
              </span>
              {/*
               * Keyed by the icon code so a changed forecast retries an icon that failed
               * earlier; the same code at the same position keeps its failed state.
               */}
              <WeatherIcon key={hour.icon} icon={hour.icon} />
              <span className="textFooter" style={footerFont}>
                {hour.temp}°C
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The forecast icon, from `openweathermap.org` — the one asset in this app that is not in the
 * bundle.
 *
 * Signage displays sit on building networks that are often allowed to reach the broker and
 * nothing else, so this request is the most likely thing on the screen to fail. A failed
 * `<img>` renders the browser's broken-image glyph, which on a wall reads as a broken display;
 * an hour with a temperature and no icon reads as weather. So the element is removed on the
 * first `error` and the failure is logged once — once per icon code, not once per render, or a
 * display that runs for weeks writes a console entry every few seconds forever.
 */
function WeatherIcon({ icon }: { icon: string }) {
  const [failed, setFailed] = useState(false);

  if (failed || !icon) return null;

  return (
    <img
      className="object-fill min-w-min"
      src={`${ICON_BASE_URL}${icon}@2x.png`}
      alt=""
      onError={() => {
        warnOnce(
          `weather-icon:${icon}`,
          '[signage] weather icon "%s" failed to load from %s — rendering the hour and ' +
            'temperature without it. Check whether this display can reach openweathermap.org.',
          icon,
          ICON_BASE_URL,
        );
        setFailed(true);
      }}
    />
  );
}

/**
 * The six forecast cells, or none before the first message / on a shape mismatch.
 *
 * The return type is the contract with `weatherTransform`: if the transform's `WeatherHour`
 * ever gains a field this schema does not describe, this line stops compiling.
 */
function readForecast(data: unknown, componentId: string): WeatherHour[] {
  if (data === undefined) return [];

  const parsed = forecastSchema.safeParse(data);
  if (!parsed.success) {
    warnOnce(
      `weather-widget-payload:${componentId}`,
      '[signage] WeatherWidget data is not a transformed forecast; keeping the empty state. %s',
      z.prettifyError(parsed.error),
    );
    return [];
  }
  return parsed.data;
}
